import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AppModule, Plan } from '../../core/models';
import { BillingStatus, Invoice, PixSettings } from '../../core/services/billing.service';

export const SUPER_ADMIN_API = `${environment.apiUrl}/super-admin`;
const SA_TOKEN_KEY = 'aggendai_sa_token';
const SA_EXPIRES_KEY = 'aggendai_sa_expires';

export interface SuperAdminTenant {
  id: string;
  name: string;
  slug: string;
  segment: string;
  isActive: boolean;
  trialEndsAt: string;
  createdAt: string;
  planId: string;
  planName: string;
  extraModules: AppModule[];
  disabledModules: AppModule[];
  ownerName: string | null;
  ownerEmail: string | null;
  usersCount: number;
  billingDay: number | null;
  billingStatus: BillingStatus;
  bookingBlocked: boolean;
}

export interface SuperAdminStats {
  totalTenants: number;
  activeTenants: number;
  inactiveTenants: number;
  inTrial: number;
  byPlan: { planId: string; planName: string; count: number }[];
}

export interface CreateTenantRequest {
  storeName: string;
  segment: string;
  ownerName: string;
  ownerEmail: string;
  ownerPassword: string;
  planId: string;
  trialEndsAt?: string | null;
}

export interface UpdateTenantRequest { name: string; segment: string; trialEndsAt: string; billingDay: number | null; }
export interface UpdatePlanRequest {
  name: string;
  monthlyPrice: number;
  monthlyAppointmentLimit: number | null;
  modules: AppModule[];
}

export type NoticeSeverity = 'Info' | 'Warning' | 'Danger';
export interface SuperAdminNotice {
  id: string;
  tenantId: string | null;
  tenantName: string | null;
  title: string;
  message: string;
  severity: NoticeSeverity;
  isActive: boolean;
  expiresAt: string | null;
  createdAt: string;
  dismissedCount: number;
}
export interface SaveNoticeRequest {
  tenantId: string | null;
  title: string;
  message: string;
  severity: NoticeSeverity;
  expiresAt: string | null;
}

export interface PrivacyRequest {
  id: string;
  protocol: string;
  tenantId: string;
  tenantName: string;
  type: 'Account' | 'ClientData';
  status: 'Received' | 'Completed' | 'Rejected';
  subjectName: string;
  subjectPhone: string;
  subjectEmail: string | null;
  details: string | null;
  createdAt: string;
  processedAt: string | null;
  resolutionNote: string | null;
  matchedClients: number;
  anonymizedClients: number;
  keptFiscalRecords: boolean;
  notifiedByEmail: boolean;
  notifiedByWhatsApp: boolean;
}

/** Sessão do super admin: token separado do login das lojas, guardado só na aba (sessionStorage). */
@Injectable({ providedIn: 'root' })
export class SuperAdminAuth {
  private http = inject(HttpClient);
  private router = inject(Router);

  readonly token = signal<string | null>(this.readValidToken());
  readonly isAuthenticated = computed(() => !!this.token());

  login(user: string, password: string) {
    return this.http.post<{ token: string; expiresAt: string }>(`${SUPER_ADMIN_API}/auth/login`, { user, password }).pipe(
      tap((r) => {
        sessionStorage.setItem(SA_TOKEN_KEY, r.token);
        sessionStorage.setItem(SA_EXPIRES_KEY, r.expiresAt);
        this.token.set(r.token);
      }),
    );
  }

  logout(): void {
    sessionStorage.removeItem(SA_TOKEN_KEY);
    sessionStorage.removeItem(SA_EXPIRES_KEY);
    this.token.set(null);
    this.router.navigate(['/super-admin/login']);
  }

  private readValidToken(): string | null {
    if (typeof sessionStorage === 'undefined') return null;
    const token = sessionStorage.getItem(SA_TOKEN_KEY);
    const expires = sessionStorage.getItem(SA_EXPIRES_KEY);
    if (!token || !expires || new Date(expires).getTime() <= Date.now()) return null;
    return token;
  }
}

@Injectable({ providedIn: 'root' })
export class SuperAdminApi {
  private http = inject(HttpClient);
  private base = SUPER_ADMIN_API;

  stats() { return this.http.get<SuperAdminStats>(`${this.base}/stats`); }

  tenants(search?: string, active?: boolean | null) {
    const params: Record<string, string> = {};
    if (search) params['search'] = search;
    if (active !== null && active !== undefined) params['active'] = String(active);
    return this.http.get<SuperAdminTenant[]>(`${this.base}/tenants`, { params });
  }
  createTenant(dto: CreateTenantRequest) { return this.http.post<SuperAdminTenant>(`${this.base}/tenants`, dto); }
  updateTenant(id: string, dto: UpdateTenantRequest) { return this.http.put<SuperAdminTenant>(`${this.base}/tenants/${id}`, dto); }
  setStatus(id: string, isActive: boolean) { return this.http.put<SuperAdminTenant>(`${this.base}/tenants/${id}/status`, { isActive }); }
  setPlan(id: string, planId: string) { return this.http.put<SuperAdminTenant>(`${this.base}/tenants/${id}/plan`, { planId }); }
  setModules(id: string, extraModules: AppModule[], disabledModules: AppModule[]) {
    return this.http.put<SuperAdminTenant>(`${this.base}/tenants/${id}/modules`, { extraModules, disabledModules });
  }
  deleteTenant(id: string) { return this.http.delete<void>(`${this.base}/tenants/${id}`); }

  plans() { return this.http.get<Plan[]>(`${this.base}/plans`); }
  updatePlan(id: string, dto: UpdatePlanRequest) { return this.http.put<Plan>(`${this.base}/plans/${id}`, dto); }

  notices(tenantId?: string | null) {
    const params: Record<string, string> = tenantId ? { tenantId } : {};
    return this.http.get<SuperAdminNotice[]>(`${this.base}/notices`, { params });
  }
  createNotice(dto: SaveNoticeRequest) { return this.http.post<SuperAdminNotice>(`${this.base}/notices`, dto); }
  updateNotice(id: string, dto: SaveNoticeRequest) { return this.http.put<SuperAdminNotice>(`${this.base}/notices/${id}`, dto); }
  setNoticeStatus(id: string, isActive: boolean) {
    return this.http.put<SuperAdminNotice>(`${this.base}/notices/${id}/status`, { isActive });
  }
  deleteNotice(id: string) { return this.http.delete<void>(`${this.base}/notices/${id}`); }

  privacyRequests(status?: string | null) {
    const params: Record<string, string> = status ? { status } : {};
    return this.http.get<PrivacyRequest[]>(`${this.base}/privacy-requests`, { params });
  }
  executePrivacy(id: string, note: string | null) {
    return this.http.put<PrivacyRequest>(`${this.base}/privacy-requests/${id}/execute`, { note });
  }
  rejectPrivacy(id: string, note: string) {
    return this.http.put<PrivacyRequest>(`${this.base}/privacy-requests/${id}/reject`, { note });
  }

  getPix() { return this.http.get<PixSettings>(`${this.base}/billing/pix`); }
  updatePix(dto: PixSettings) { return this.http.put<PixSettings>(`${this.base}/billing/pix`, dto); }
  invoices(tenantId?: string | null, status?: string | null) {
    const params: Record<string, string> = {};
    if (tenantId) params['tenantId'] = tenantId;
    if (status) params['status'] = status;
    return this.http.get<Invoice[]>(`${this.base}/invoices`, { params });
  }
  createInvoice(dto: { tenantId: string; description: string; amount: number; dueDate: string }) {
    return this.http.post<Invoice>(`${this.base}/invoices`, dto);
  }
  generateInvoices() { return this.http.post<{ created: number }>(`${this.base}/invoices/generate`, {}); }
  changeDueDate(id: string, dueDate: string) { return this.http.put<Invoice>(`${this.base}/invoices/${id}/due-date`, { dueDate }); }
  validateInvoice(id: string, result: 'Paid' | 'Pending', note: string | null) {
    return this.http.put<Invoice>(`${this.base}/invoices/${id}/validate`, { result, note });
  }
  cancelInvoice(id: string) { return this.http.put<Invoice>(`${this.base}/invoices/${id}/cancel`, {}); }
  downloadProof(id: string, proofId: string) {
    return this.http.get(`${this.base}/invoices/${id}/proofs/${proofId}`, { responseType: 'blob' });
  }
}
